import React, { useEffect, useState, useRef } from "react";
import { api, srcForCover, mediaUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePlayer } from "@/context/PlayerContext";
import { formatDuration, formatDate } from "@/lib/utils2";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Printer, StickyNote, Clock, Search, Bookmark, BookmarkPlus, X, GripVertical, Radio, Newspaper, Play, Headphones, ChevronUp, ChevronDown, Trash2, CheckSquare } from "lucide-react";
import { toast } from "sonner";

function Highlight({ text, term }) {
  const t = (term || "").trim();
  if (!t) return <>{text}</>;
  const safe = t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const parts = String(text || "").split(new RegExp(`(${safe})`, "ig"));
  return (
    <>
      {parts.map((p, i) =>
        p.toLowerCase() === t.toLowerCase()
          ? <mark key={i} className="bg-brand-dough/60 text-brand-ink rounded px-0.5">{p}</mark>
          : <React.Fragment key={i}>{p}</React.Fragment>
      )}
    </>
  );
}

export default function NotesExport() {
  const { user } = useAuth();
  const { play } = usePlayer();
  const [data, setData] = useState(null);
  const [query, setQuery] = useState("");
  const [searches, setSearches] = useState([]);
  const [everywhere, setEverywhere] = useState(null);
  const dragIndex = useRef(null);
  const searchToken = useRef(0);
  const [reading, setReading] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [selectMode, setSelectMode] = useState(false);

  const reload = () => api.get("/notes/mine").then(({ data }) => setData(data)).catch(() => setData({ groups: [], total_notes: 0 }));

  const toggleSelect = (id, e) => {
    e.stopPropagation();
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const clearSelection = () => { setSelected(new Set()); setSelectMode(false); };

  const bulkDelete = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    try {
      const { data } = await api.post("/notes/bulk-delete", { ids });
      toast.success(`Deleted ${data.deleted} note${data.deleted === 1 ? "" : "s"}`);
      clearSelection();
      reload();
    } catch { toast.error("Couldn't delete notes."); }
  };

  useEffect(() => {
    api.get("/notes/mine").then(({ data }) => setData(data)).catch(() => setData({ groups: [], total_notes: 0 }));
    api.get("/notes/searches").then(({ data }) => setSearches(data.searches)).catch(() => {});
  }, []);

  // Search Everywhere — episodes + articles matches (debounced, stale-safe)
  useEffect(() => {
    const term = query.trim();
    if (!term) { setEverywhere(null); return; }
    const token = ++searchToken.current;
    const t = setTimeout(() => {
      api.get(`/search?q=${encodeURIComponent(term)}`)
        .then(({ data }) => { if (token === searchToken.current) setEverywhere(data); })
        .catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  const pinSearch = async () => {
    const q = query.trim();
    if (!q) return;
    try {
      const { data } = await api.post("/notes/searches", { query: q });
      if (data.already_saved) toast.info("Already pinned");
      else { setSearches((s) => [...s, data.search]); toast.success("Search pinned"); }
    } catch { toast.error("Couldn't pin search."); }
  };

  const removeSearch = async (id, e) => {
    e.stopPropagation();
    try { await api.delete(`/notes/searches/${id}`); setSearches((s) => s.filter((x) => x.id !== id)); }
    catch { toast.error("Couldn't remove."); }
  };

  const persistOrder = async (next, prev) => {
    setSearches(next);
    try { await api.post("/notes/searches/reorder", { ids: next.map((s) => s.id) }); }
    catch { setSearches(prev); toast.error("Couldn't save order."); }
  };

  const onDrop = async (dropIndex) => {
    const from = dragIndex.current;
    dragIndex.current = null;
    if (from == null || from === dropIndex) return;
    const prev = searches;
    const next = [...searches];
    const [moved] = next.splice(from, 1);
    next.splice(dropIndex, 0, moved);
    await persistOrder(next, prev);
  };

  const move = async (index, dir, e) => {
    e.stopPropagation();
    const target = index + dir;
    if (target < 0 || target >= searches.length) return;
    const prev = searches;
    const next = [...searches];
    [next[index], next[target]] = [next[target], next[index]];
    await persistOrder(next, prev);
  };

  const isPinned = !!query.trim() && searches.some((s) => s.query.toLowerCase() === query.trim().toLowerCase());

  const q = query.trim().toLowerCase();
  const filteredGroups = !data ? [] : (!q ? data.groups : data.groups
    .map((g) => {
      const titleMatch = g.episode_title.toLowerCase().includes(q);
      const notes = titleMatch ? g.notes : g.notes.filter((n) => n.text.toLowerCase().includes(q));
      return notes.length ? { ...g, notes } : null;
    })
    .filter(Boolean));
  const matchCount = filteredGroups.reduce((s, g) => s + g.notes.length, 0);

  const printSheet = () => {
    if (!data) return;
    const today = new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" });
    const esc = (s) => String(s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const groupsHtml = data.groups.map((g) => `
      <section class="ep">
        <h2>${esc(g.episode_title)}</h2>
        ${g.hosts && g.hosts.length ? `<p class="hosts">${esc(g.hosts.join(" & "))}</p>` : ""}
        <ul>
          ${g.notes.map((n) => `<li>${n.timestamp_seconds != null ? `<span class="ts">${formatDuration(n.timestamp_seconds)}</span>` : ""}<span>${esc(n.text)}</span></li>`).join("")}
        </ul>
      </section>`).join("");
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>The Spread — Shift Prep Notes</title>
      <style>
        @page { margin: 24mm 18mm; }
        body { font-family: Georgia, serif; color: #1B241E; line-height: 1.5; }
        header { border-bottom: 3px solid #4A7045; padding-bottom: 12px; margin-bottom: 20px; }
        .brand { color: #4A7045; font-size: 26px; font-weight: bold; letter-spacing: -0.5px; }
        .meta { color: #5C6B61; font-size: 13px; margin-top: 4px; }
        .ep { margin-bottom: 22px; page-break-inside: avoid; }
        .ep h2 { font-size: 18px; margin: 0 0 2px; color: #1B241E; }
        .hosts { color: #5C6B61; font-size: 12px; font-style: italic; margin: 0 0 8px; }
        ul { list-style: none; padding: 0; margin: 0; }
        li { display: flex; gap: 10px; padding: 6px 0; border-bottom: 1px dashed #E5E0D8; font-family: Arial, sans-serif; font-size: 14px; }
        .ts { background: #EAF0E9; color: #4A7045; font-weight: bold; border-radius: 10px; padding: 1px 8px; font-size: 12px; white-space: nowrap; height: fit-content; }
        .empty { color: #5C6B61; font-style: italic; }
        footer { margin-top: 30px; color: #9aa79d; font-size: 11px; text-align: center; }
      </style></head><body>
      <header>
        <div class="brand">The Spread — Shift Prep Sheet</div>
        <div class="meta">${esc(user.first_name)} ${esc(user.last_name)} · Store ${esc(user.store_number)} · ${today} · ${data.total_notes} note${data.total_notes === 1 ? "" : "s"}</div>
      </header>
      ${data.groups.length ? groupsHtml : '<p class="empty">No notes yet — jot notes on any episode to build your shift-prep sheet.</p>'}
      <footer>Private coaching notes · The Spread · Panera Bread</footer>
      </body></html>`;
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 350);
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-4xl">
      <PageHeader title="My Notes" subtitle="Every note you've jotted, grouped by episode. Print a tidy shift-prep sheet to bring to the floor.">
        {data && data.total_notes > 0 && (
          <Button variant="outline" onClick={() => { setSelectMode((v) => !v); setSelected(new Set()); }} className="rounded-full border-brand-line" data-testid="select-notes-toggle">
            <CheckSquare className="w-4 h-4 mr-2" /> {selectMode ? "Cancel" : "Select"}
          </Button>
        )}
        <Button onClick={printSheet} disabled={!data || data.total_notes === 0} className="rounded-full brand-btn" data-testid="print-notes-button">
          <Printer className="w-4 h-4 mr-2" /> Print / Export
        </Button>
      </PageHeader>

      {data && data.groups.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center gap-2 max-w-md">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-muted" />
              <Input
                data-testid="notes-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search your notes and episodes…"
                className="pl-9 border-brand-line"
              />
            </div>
            <Button
              type="button"
              variant="outline"
              disabled={!query.trim() || isPinned}
              onClick={pinSearch}
              className="rounded-full border-brand-line shrink-0 disabled:opacity-50"
              data-testid="pin-search-button"
              title={isPinned ? "Already pinned" : "Pin this search"}
            >
              {isPinned ? <Bookmark className="w-4 h-4 fill-brand text-brand" /> : <BookmarkPlus className="w-4 h-4" />}
            </Button>
          </div>
          {q && <p className="text-xs text-brand-muted mt-1.5" data-testid="notes-search-count">{matchCount} match{matchCount === 1 ? "" : "es"}</p>}
          {searches.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 mt-3" data-testid="saved-searches">
              <span className="text-xs text-brand-muted flex items-center gap-1"><Bookmark className="w-3.5 h-3.5" /> Pinned:</span>
              {searches.map((s, idx) => {
                const active = query.trim().toLowerCase() === s.query.toLowerCase();
                return (
                  <div
                    key={s.id}
                    draggable
                    onDragStart={() => { dragIndex.current = idx; }}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => onDrop(idx)}
                    onClick={() => setQuery(s.query)}
                    data-testid={`saved-search-${s.id}`}
                    className={`group inline-flex items-center gap-1 rounded-full border pl-1.5 pr-2 py-1 text-sm cursor-pointer transition-colors ${active ? "bg-brand text-white border-brand" : "border-brand-line text-brand-ink hover:bg-brand-sidebar"}`}
                  >
                    <GripVertical className={`w-3.5 h-3.5 cursor-grab hidden sm:block ${active ? "text-white/70" : "text-brand-muted"}`} data-testid={`drag-search-${s.id}`} />
                    <span className="flex flex-col -space-y-1 sm:hidden">
                      <button onClick={(e) => move(idx, -1, e)} disabled={idx === 0} className={`disabled:opacity-30 ${active ? "text-white/80" : "text-brand-muted"}`} data-testid={`move-up-${s.id}`} aria-label="Move up"><ChevronUp className="w-3 h-3" /></button>
                      <button onClick={(e) => move(idx, 1, e)} disabled={idx === searches.length - 1} className={`disabled:opacity-30 ${active ? "text-white/80" : "text-brand-muted"}`} data-testid={`move-down-${s.id}`} aria-label="Move down"><ChevronDown className="w-3 h-3" /></button>
                    </span>
                    <span className="px-1">{s.query}</span>
                    <X onClick={(e) => removeSearch(s.id, e)} className="w-3.5 h-3.5 opacity-70 hover:opacity-100" data-testid={`remove-search-${s.id}`} />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {q && everywhere && (everywhere.episodes.length > 0 || everywhere.articles.length > 0) && (
        <div className="mb-6 rounded-2xl border border-brand-line bg-brand-sidebar/40 p-5" data-testid="search-everywhere">
          <p className="text-xs uppercase tracking-widest text-brand-muted mb-3">Also found across The Spread</p>
          {everywhere.episodes.length > 0 && (
            <div className="mb-4">
              <p className="text-sm font-semibold text-brand-ink flex items-center gap-1.5 mb-2"><Radio className="w-4 h-4 text-brand" /> Episodes</p>
              <div className="space-y-2">
                {everywhere.episodes.map((ep) => (
                  <div key={ep.id} className="flex items-center gap-3 rounded-xl bg-white border border-brand-line p-2.5" data-testid={`search-episode-${ep.id}`}>
                    <img src={srcForCover(ep)} alt="" className="h-10 w-10 rounded-lg object-cover border border-brand-line" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-brand-ink truncate"><Highlight text={ep.title} term={query} /></p>
                      {ep.description && <p className="text-xs text-brand-muted line-clamp-1"><Highlight text={ep.description} term={query} /></p>}
                      <div className="flex items-center gap-2">
                        <p className="text-xs text-brand-muted truncate">{ep.hosts?.join(" & ") || "The Spread"}</p>
                        {ep.listened && (
                          <span className="inline-flex items-center gap-1 text-[11px] font-medium text-brand shrink-0" data-testid={`search-episode-listened-${ep.id}`}>
                            <Headphones className="w-3 h-3" /> Listened
                          </span>
                        )}
                      </div>
                    </div>
                    <Button size="sm" onClick={() => play(ep)} className="rounded-full brand-btn shrink-0" data-testid={`search-play-${ep.id}`}>
                      <Play className="w-3.5 h-3.5 mr-1" /> Play
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
          {everywhere.articles.length > 0 && (
            <div>
              <p className="text-sm font-semibold text-brand-ink flex items-center gap-1.5 mb-2"><Newspaper className="w-4 h-4 text-brand" /> Articles</p>
              <div className="space-y-2">
                {everywhere.articles.map((a) => (
                  <button key={a.id} onClick={() => setReading(a)} className="w-full text-left flex items-center gap-3 rounded-xl bg-white border border-brand-line p-2.5 hover:bg-brand-sidebar transition-colors" data-testid={`search-article-${a.id}`}>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-brand-ink truncate"><Highlight text={a.title} term={query} /></p>
                      <p className="text-xs text-brand-muted line-clamp-2"><Highlight text={a.body} term={query} /></p>
                    </div>
                    <Newspaper className="w-4 h-4 text-brand-muted shrink-0" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {!data ? (
        <div className="space-y-4">
          {[1, 2].map((i) => (
            <div key={i} className="rounded-2xl border border-brand-line bg-white p-6 space-y-3">
              <Skeleton className="h-5 w-1/2" /><Skeleton className="h-4 w-full" /><Skeleton className="h-4 w-3/4" />
            </div>
          ))}
        </div>
      ) : data.groups.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-brand-line p-12 text-center" data-testid="notes-empty">
          <StickyNote className="w-8 h-8 text-brand-muted mx-auto" />
          <p className="font-display text-2xl text-brand-ink mt-3">No notes yet</p>
          <p className="text-brand-muted mt-1">Open any episode and tap the note icon to capture shift lessons.</p>
        </div>
      ) : filteredGroups.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-brand-line p-12 text-center" data-testid="notes-no-match">
          <Search className="w-8 h-8 text-brand-muted mx-auto" />
          <p className="font-display text-2xl text-brand-ink mt-3">No matches</p>
          <p className="text-brand-muted mt-1">No notes contain "{query}". Try a different search.</p>
        </div>
      ) : (
        <div className="space-y-5" data-testid="notes-groups">
          {filteredGroups.map((g) => (
            <div key={g.episode_id} className="rounded-2xl border border-brand-line bg-white p-6" data-testid={`notes-group-${g.episode_id}`}>
              <h3 className="font-display text-2xl text-brand-ink"><Highlight text={g.episode_title} term={query} /></h3>
              {g.hosts?.length > 0 && <p className="text-sm text-brand-muted">{g.hosts.join(" & ")}</p>}
              <ul className="mt-4 space-y-2">
                {g.notes.map((n) => (
                  <li key={n.id}
                    onClick={selectMode ? (e) => toggleSelect(n.id, e) : undefined}
                    className={`flex items-start gap-3 py-2 border-b border-brand-line last:border-0 ${selectMode ? "cursor-pointer -mx-2 px-2 rounded-lg hover:bg-brand-sidebar/50" : ""} ${selected.has(n.id) ? "bg-brand/[0.06]" : ""}`}
                    data-testid={`note-item-${n.id}`}>
                    {selectMode && (
                      <Checkbox checked={selected.has(n.id)} onCheckedChange={() => {}} className="mt-0.5 shrink-0" data-testid={`note-checkbox-${n.id}`} />
                    )}
                    {n.timestamp_seconds != null && (
                      <span className="shrink-0 mt-0.5 inline-flex items-center gap-1 text-xs font-semibold text-brand bg-brand/10 rounded-full px-2 py-0.5">
                        <Clock className="w-3 h-3" /> {formatDuration(n.timestamp_seconds)}
                      </span>
                    )}
                    <span className="text-brand-ink whitespace-pre-line"><Highlight text={n.text} term={query} /></span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {/* Bulk selection action bar */}
      {selectMode && selected.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 flex items-center gap-4 rounded-full bg-brand-ink text-white pl-5 pr-2 py-2 shadow-xl" data-testid="bulk-action-bar">
          <span className="text-sm font-medium">{selected.size} selected</span>
          <button onClick={clearSelection} className="text-sm text-white/70 hover:text-white" data-testid="bulk-clear">Cancel</button>
          <Button onClick={bulkDelete} className="rounded-full bg-brand-terracotta hover:bg-brand-terracotta h-9" data-testid="bulk-delete-button">
            <Trash2 className="w-4 h-4 mr-1.5" /> Delete
          </Button>
        </div>
      )}

      {/* Article reader */}
      <Dialog open={!!reading} onOpenChange={(o) => !o && setReading(null)}>
        <DialogContent className="max-w-2xl border-brand-line max-h-[85vh] overflow-y-auto scrollbar-thin">
          {reading && (
            <div data-testid="notes-article-reader">
              <DialogHeader><DialogTitle className="font-display text-3xl text-left">{reading.title}</DialogTitle></DialogHeader>
              <p className="text-sm text-brand-muted">{reading.author_name}{reading.created_at ? ` · ${formatDate(reading.created_at)}` : ""}</p>
              {reading.photos && reading.photos.length > 0 && (
                <img src={reading.photos[0].startsWith("http") ? reading.photos[0] : mediaUrl(reading.photos[0])} alt="" className="rounded-xl w-full h-56 object-cover mt-4 border border-brand-line" />
              )}
              <div className="mt-4 text-brand-ink leading-relaxed whitespace-pre-line"><Highlight text={reading.body} term={query} /></div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
